import { captureException } from '@sentry/react';

interface DownloadFileProps {
  postApi: () => Promise<Response>;
  onError: (error: Response | Error) => void;
  onSuccess: (response: Response) => void;
  onLoading: (loading: boolean) => void;
}

interface FileInfo {
  download: () => Promise<void>;
}

/**
 * Extracts filename from Content-Disposition header
 */
const extractFilename = (disposition: string | null): string => {
  const fallbackFilename = 'download';
  if (!disposition) {
    return fallbackFilename; // Fallback filename
  }

  try {
    // Look for filename*= (RFC 5987) or filename= patterns
    const filenameStarMatch = disposition.match(/filename\*=UTF-8''(.+)/i);
    if (filenameStarMatch) {
      return decodeURIComponent(filenameStarMatch[1]);
    }

    const filenameMatch = disposition.match(/filename=([^;]+)/i);
    if (filenameMatch) {
      return filenameMatch[1].replace(/['"]/g, '');
    }

    return fallbackFilename;
  } catch {
    return fallbackFilename;
  }
};

/**
 * Triggers download of a blob with the specified filename
 */
const triggerDownload = (blob: Blob, filename: string): void => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const useDownloadFile = ({
  postApi,
  onError,
  onLoading,
  onSuccess,
}: DownloadFileProps): FileInfo => {
  const download = async (): Promise<void> => {
    try {
      onLoading(true);

      const response = await postApi();

      if (!response.ok) {
        onLoading(false);
        onError(response);
        return;
      }

      const disposition = response.headers.get('Content-Disposition');
      const filename = extractFilename(disposition);
      const blob = await response.blob();

      triggerDownload(blob, filename);

      onLoading(false);
      onSuccess(response);
    } catch (error) {
      onLoading(false);
      const errorToReport =
        error instanceof Error ? error : new Error('Unknown download error');
      captureException(errorToReport);
      onError(errorToReport);
    }
  };

  return { download };
};
